import React, { useEffect, useState } from "react";
import {
    EditarFormButtonsWrapper,
    EditarFormDivider,
} from "./styles";
import { Form, Input, Select, Button, message, Spin, Switch, Upload } from "antd";
import { OptionValue } from "../../../../../shared/dto/OptionValue";
import RoleProvider from "../../../../../shared/provider/RoleProvider";
import {
    FaUser,
    FaEnvelope,
    FaIdCard,
} from "react-icons/fa";
import { CreateUserDto } from "../../../../../shared/dto/CreateUserDto";
import AuthProvider from "../../../../../shared/provider/AuthProvider";
import validateUserCreationDto from "./validation/validateUserCreationDto";
import UserProvider from "../../../../../shared/provider/UserProvider";
import { UploadOutlined } from '@ant-design/icons';

const { Option } = Select;

function AdicionarUsuarioForm({
    setOpen,
    isLoading = false,
    setLoading,
    setSuccessMessage,
    requireUserList,
}: {
    setOpen: React.Dispatch<boolean>;
    isLoading?: boolean;
    setLoading: React.Dispatch<boolean>;
    setSuccessMessage: React.Dispatch<string>;
    requireUserList: () => Promise<void>;
}) {
    const [form] = Form.useForm();
    const authProvider = new AuthProvider();
    const [roleOptions, setRoleOptions] = useState([] as Array<OptionValue>);
    const userProvider = new UserProvider();

    const roleProvider = new RoleProvider();

    const ActiveOptions = [
        { value: "true", label: "Ativo" },
        { value: "false", label: "Inativo" },
    ];

    async function requireRoleOptions() {
        try {
            const data = await roleProvider.getAll();
            if (!data) return [] as Array<OptionValue>;

            setRoleOptions(data.data);
            return data;
        } catch (error) {
            console.error("Erro ao buscar roles:", error);
            return [] as Array<OptionValue>;
        }
    }

    async function onInit() {
        await requireRoleOptions();
    }

    useEffect(() => {
        onInit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function createUser(data: CreateUserDto) {
        setLoading(true);
        const response = await authProvider.register(data);
        if (response) {
            await requireUserList();
            setSuccessMessage("Usuário cadastrado com sucesso!");
        }
        setLoading(false);
    }

    async function handleBatchUpload({ file }: { file: File }) {
        if (file.type !== "text/csv") {
            message.error("O arquivo deve ser um CSV.");
            return;
        }

        setLoading(true);
        try {
            const fileObject = { data: file, name: file.name, type: file.type, size: file.size };
            await userProvider.batchUpload(fileObject);
            await requireUserList();
            setSuccessMessage("Usuários cadastrados com sucesso!");
        } catch (error) {
            message.error("Erro ao fazer upload do CSV.");
        } finally {
            setLoading(false);
        }
    }

    function handleSubmit(values: any) {
        const { nome, email, cpf, role, ativo } = values;

        const erros = validateUserCreationDto({
            name: nome,
            email,
            tenantId: "",
            roleId: role,
            document: cpf,
        });

        if (erros.length) {
            erros.forEach((erro) => message.error(erro));
            return;
        }

        const nomeCompleto = nome.trim();
        const nomeArray = nomeCompleto.split(" ");
        const nomeUsuario = nomeArray[0];
        const sobrenomeUsuario = nomeArray.slice(1).join(" ");

        const newUser = {
            nome: nomeUsuario,
            sobrenome: sobrenomeUsuario,
            email,
            cpf,
            roleId: role,
            ativo: ativo,
        };

        createUser(newUser);
    }

    return (
        <Form form={form} onFinish={handleSubmit} requiredMark={false} layout="vertical">
            <Form.Item
                name="nome"
                label={<b>Nome</b>}
                rules={[{ required: true, message: "Por favor, insira o nome completo" }]}
            >
                <Input placeholder="Nome completo" />
            </Form.Item>

            <Form.Item
                name="cpf"
                label={<b>CPF</b>}
                rules={[
                    { required: true, message: "Por favor, insira o CPF" },
                    { pattern: /^[0-9.-]+$/, message: "CPF deve conter apenas números, pontos e hífens" }
                ]}
            >
                <Input placeholder="Digite aqui..." />
            </Form.Item>

            <Form.Item>
                <Form.Item
                    name="email"
                    label={<b>E-mail</b>}
                    rules={[{ required: true, message: "Por favor, insira o e-mail" }]}
                    style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
                >
                    <Input placeholder="Ex: seuemail@gov.br" />
                </Form.Item>

                <Form.Item
                    name="role"
                    label={<b>Perfil</b>}
                    rules={[{ required: true, message: "Por favor, selecione o perfil" }]}
                    style={{ display: 'inline-block', width: 'calc(50% - 8px)', marginLeft: '16px' }}
                >
                    <Select placeholder="Selecione um perfil">
                        {roleOptions.map((role) => (
                            <Option key={role.value} value={role.value}>
                                {role.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form.Item>

            <Form.Item
                name="ativo"
                label={<b>Status</b>}
                valuePropName="checked"
            >
                <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
            </Form.Item>

            <EditarFormDivider />

            <EditarFormButtonsWrapper>
                <div style={{ float: 'left' }}>
                    <Upload
                        accept=".csv"
                        customRequest={({ file }) => handleBatchUpload({ file: file as File })}
                        showUploadList={false}
                    >
                        <Button
                            type="default"
                            icon={<UploadOutlined />}
                            style={{ borderRadius: '20px' }}
                        >
                            Cadastro de usuários em lote
                        </Button>
                    </Upload>
                </div>
                <div style={{ float: 'right' }}>
                    <Button
                        type="default"
                        onClick={() => {
                            form.resetFields();
                        }}
                        style={{ borderRadius: '20px', marginRight: '8px' }}
                    >
                        Limpar todos os campos
                    </Button>

                    <Button
                        type="primary"
                        htmlType="submit"
                        disabled={isLoading}
                        style={{ borderRadius: '20px' }}
                    >
                        {isLoading ? <Spin /> : "Cadastrar novo usuário"}
                    </Button>
                </div>
            </EditarFormButtonsWrapper>
        </Form>
    );
}

export default AdicionarUsuarioForm;
